import TimesIcon from "@/assets/colorable/times.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import clsxm from "@/lib/clsxm";
import type {TButtonStyle} from "@/store/types";
import type {FC, FunctionComponent, ReactNode, SVGProps} from "react";
import {useTranslation} from "react-i18next";

const TONE_STYLES: Record<string, {confirmButtonStyle: TButtonStyle; header: string; icon: string}> = {
  danger: {header: "bg-danger-light-red", icon: "text-danger-medium-red", confirmButtonStyle: "Danger"},
  warning: {header: "bg-warning-light-yellow", icon: "text-warning-dark-yellow", confirmButtonStyle: "Gradient"},
};

const ConfirmationModal: FC<{
  Icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  cancelButtonText?: string;
  confirmButtonStyle?: TButtonStyle;
  confirmButtonText: string;
  description?: ReactNode;
  descriptionClassName?: string;
  iconSlot?: ReactNode;
  isPending?: boolean;
  onCancelButtonClick?: () => void;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  tone?: keyof typeof TONE_STYLES;
}> = ({
  Icon,
  cancelButtonText,
  confirmButtonStyle,
  confirmButtonText,
  description,
  descriptionClassName,
  iconSlot,
  isPending,
  onCancelButtonClick,
  onClose,
  onConfirm,
  title,
  tone = "danger",
}) => {
  const {t} = useTranslation("common");
  const toneStyle = TONE_STYLES[tone];

  const handleClose = () => {
    if (!isPending) onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      shouldHideModalTitle
      backgroundClassName="w-[95vw] sm:w-135 md:w-135 max-h-none rounded-xl"
      className="gap-0 p-0"
      customBottomRow={
        <div className="border-neutral-grey-light-dark flex gap-2.5 border-t px-10 py-5">
          <Button
            buttonStyle="White"
            className="h-15 flex-1"
            onClick={onCancelButtonClick ?? handleClose}
            isDisabled={isPending}>
            {cancelButtonText ?? t("cancel")}
          </Button>
          <Button
            buttonStyle={confirmButtonStyle ?? toneStyle.confirmButtonStyle}
            className="h-15 flex-1"
            onClick={onConfirm}
            isLoading={isPending}>
            {confirmButtonText}
          </Button>
        </div>
      }>
      <div className={clsxm("relative flex flex-col items-center gap-5 p-5", toneStyle.header)}>
        <button className="absolute top-5 right-5" onClick={handleClose} disabled={isPending}>
          <TimesIcon className="text-neutral-grey-light size-3.5" />
        </button>
        <div className="flex flex-col items-center gap-3">
          {iconSlot ??
            (Icon && (
              <div className="flex size-15 items-center justify-center rounded-full bg-white">
                <Icon className={clsxm("size-7", toneStyle.icon)} />
              </div>
            ))}
          <span className="text-neutral-black text-center text-xl font-semibold">{title}</span>
        </div>
        {!!description && (
          <span className={clsxm("text-neutral-black text-xs", descriptionClassName)}>{description}</span>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
